import { useEffect, useRef } from 'react';

/**
 * Rain on the window behind Scripture AI.
 *
 * Two layers doing different jobs: streaks falling at a slight angle, and
 * droplets that cling to the glass and slide when they get heavy. The second
 * one is what stops it reading as a screensaver — real rain on a window is
 * mostly still, with occasional runs.
 *
 * Canvas rather than DOM, for the same reason as the particle field: a hundred
 * animated elements is a hundred composited layers, and one canvas is one.
 *
 * Three things keep it from being a battery tax:
 *   - the loop stops entirely while the tab is hidden;
 *   - it never starts under `prefers-reduced-motion`;
 *   - drop counts scale with area, so a phone draws a fraction of a desktop's.
 */

/**
 * How much rain, and why there is a floor as well as a ceiling.
 *
 * Scaling purely by area gives a phone a tenth of a desktop's drops, because a
 * phone screen is a third of a megapixel. That is not "less rain for a smaller
 * window", it is seven streaks and no weather at all. Rain should feel about as
 * heavy whatever it is falling on, so the count is area-scaled between a floor
 * that still reads as rain and a ceiling that keeps a large screen cheap.
 */
const STREAKS = { min: 30, perMegapixel: 60, max: 90 };
const BEADS = { min: 22, perMegapixel: 45, max: 60 };

const countFor = ({ min, perMegapixel, max }, area) =>
  Math.max(min, Math.min(max, Math.round((area / 1_000_000) * perMegapixel)));

/** A few degrees off vertical. Perfectly vertical rain looks like a barcode. */
const SLANT = 0.14;

const random = (min, max) => min + Math.random() * (max - min);

export default function RainField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (calm?.matches) return undefined;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let streaks = [];
    let beads = [];
    let frame = 0;
    let last = 0;

    const spawnStreak = (offscreen) => ({
      x: random(-0.1, 1.1) * width,
      y: offscreen ? random(-height, 0) : random(0, height),
      // Length and speed travel together, which is what sells depth: the
      // nearer drops are longer, faster and brighter.
      depth: random(0.35, 1),
      length: 0,
      speed: 0,
    });

    const sizeStreak = (streak) => {
      streak.length = 8 + streak.depth * 26;
      streak.speed = 260 + streak.depth * 560;
      return streak;
    };

    const spawnBead = () => ({
      x: random(0, width),
      y: random(0, height),
      r: random(0.9, 2.6),
      // How long it clings before it runs. Most never do within their life.
      hold: random(0.4, 9),
      slide: 0,
      trail: 0,
    });

    const resize = () => {
      // Cap the pixel ratio: a 3× phone screen triples the fill cost for a
      // detail nobody can see in a blurred streak.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const area = width * height;
      const streakCount = countFor(STREAKS, area);
      const beadCount = countFor(BEADS, area);

      streaks = Array.from({ length: streakCount }, () => sizeStreak(spawnStreak(false)));
      beads = Array.from({ length: beadCount }, spawnBead);
    };

    const draw = (now) => {
      const elapsed = Math.min(0.05, last ? (now - last) / 1000 : 0.016);
      last = now;

      ctx.clearRect(0, 0, width, height);

      // ── The falling rain ──────────────────────────────────────────────────
      ctx.lineCap = 'round';
      for (const streak of streaks) {
        streak.y += streak.speed * elapsed;
        streak.x += streak.speed * SLANT * elapsed;

        if (streak.y - streak.length > height) {
          Object.assign(streak, sizeStreak(spawnStreak(true)));
          continue;
        }

        ctx.beginPath();
        ctx.strokeStyle = `rgba(178, 198, 226, ${0.05 + streak.depth * 0.16})`;
        ctx.lineWidth = 0.6 + streak.depth * 1.1;
        ctx.moveTo(streak.x, streak.y);
        ctx.lineTo(streak.x - streak.length * SLANT, streak.y - streak.length);
        ctx.stroke();
      }

      // ── Beads on the glass ────────────────────────────────────────────────
      for (const bead of beads) {
        bead.hold -= elapsed;

        if (bead.hold <= 0) {
          // Once it runs it accelerates, and leaves a shrinking tail behind it.
          bead.slide += (26 + bead.r * 34) * elapsed;
          bead.y += bead.slide * elapsed;
          bead.trail = Math.min(38, bead.trail + bead.slide * elapsed * 0.9);
          if (bead.y - bead.trail > height) Object.assign(bead, spawnBead());
        }

        if (bead.trail > 1) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(178, 198, 226, ${0.05 * (bead.r / 2.6)})`;
          ctx.lineWidth = bead.r * 0.9;
          ctx.moveTo(bead.x, bead.y);
          ctx.lineTo(bead.x, bead.y - bead.trail);
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.fillStyle = `rgba(196, 214, 238, ${0.1 + bead.r * 0.05})`;
        ctx.arc(bead.x, bead.y, bead.r, 0, Math.PI * 2);
        ctx.fill();

        // A highlight at the top left of each bead, which is the whole reason
        // a circle reads as water rather than as a dot.
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 255, 255, ${0.06 + bead.r * 0.03})`;
        ctx.arc(bead.x - bead.r * 0.3, bead.y - bead.r * 0.35, bead.r * 0.34, 0, Math.PI * 2);
        ctx.fill();
      }

      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (frame) return;
      last = 0;
      frame = requestAnimationFrame(draw);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const onVisibility = () => (document.hidden ? stop() : start());

    resize();
    start();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="rain" aria-hidden="true" />;
}
